import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Camera as CamIcon,
  Pencil,
  Plus,
  RefreshCw,
  ScanEye,
  Search,
  Trash2,
} from "lucide-react";

import {
  api,
  ApiError,
  type Camera,
  type CameraCapacity,
  type CameraWriteResult,
  type Nvr,
  type Store,
} from "../../api";
import { FieldLabel, InfoHint } from "../../components/InfoHint";
import { Modal } from "../../components/Modal";
import { FisheyeCustomViews, type DraftCustomView } from "./FisheyeCustomViews";
import { FisheyeDialog, MOUNT_LABEL } from "./FisheyeDialog";
import {
  DEFAULT_LENS,
  VIEW_PRESETS,
  isCalibrated,
  samePose,
  type Lens,
  type Mount,
  type Pose,
} from "../../fisheye/lens";
import { SearchableSelect } from "../../components/SearchableSelect";
import { useScope } from "../../components/ScopeContext";
import { searchItems } from "../../fuzzy";
import { useRole } from "../../hooks/useRole";
import {
  BRANDS_WITH_SUB_STREAM,
  INGEST_FPS_OPTIONS,
  MAX_HEIGHT_OPTIONS,
  applyRtspCredentials,
  buildBulkUrls,
  deriveCameraId,
  EmptyState,
  humanizeApplyErrors,
  Note,
  SettingsSection,
  splitRtspCredentials,
} from "./shared";

function parseChannelList(text: string, exclude: number | null | undefined): number[] {
  const skip = exclude ?? 0;
  const seen = new Set<number>();
  const out: number[] = [];
  for (const part of text.split(/[,\s]+/)) {
    if (!part) continue;
    const n = Number(part);
    if (!Number.isInteger(n) || n < 1 || n === skip || seen.has(n)) continue;
    seen.add(n);
    out.push(n);
  }
  return out;
}

const emptyCamera: Camera = {
  id: "",
  name: "",
  rtsp_url: "",
  sub_rtsp_url: "",
  nvr_id: null,
  channel: null,
  // Per-camera capture knobs are no longer surfaced; the backend still expects
  // them, so send sensible defaults.
  fps: 5,
  sample_interval_s: 5,
  enabled: true,
  store: "",
  role: "ai",
  max_height: 0,
  ingest_max_fps: 0,
  people_description: null,
  activity_description: null,
};

export function CamerasSection({ canEdit }: { canEdit: boolean }) {
  const qc = useQueryClient();
  const { role } = useRole();
  const { scope, scopeParams, scopeKey, scopeLabel, stores } = useScope();

  const cams = useQuery({
    queryKey: ["cameras", scopeKey],
    queryFn: () => api.cameras.list(scopeParams),
  });
  const allCams = useQuery({ queryKey: ["cameras", "all"], queryFn: () => api.cameras.list() });
  const capacity = useQuery({
    queryKey: ["camera-capacity"],
    queryFn: api.cameras.capacity,
  });
  const nvrs = useQuery({ queryKey: ["nvrs"], queryFn: api.nvrs.list });

  const [draft, setDraft] = useState<Camera | null>(null);
  const [fisheye, setFisheye] = useState<{ id: string; focus?: string } | null>(null);
  const [bulk, setBulk] = useState(false);
  const [query, setQuery] = useState("");
  const [lastWrite, setLastWrite] = useState<CameraWriteResult | null>(null);
  const [writeError, setWriteError] = useState<string | null>(null);

  const onWrote = (r: CameraWriteResult) => {
    setWriteError(null);
    setLastWrite(r);
    qc.invalidateQueries({ queryKey: ["cameras"] });
    qc.invalidateQueries({ queryKey: ["camera-capacity"] });
    qc.invalidateQueries({ queryKey: ["stores"] });
    qc.invalidateQueries({ queryKey: ["profiles"] });
    qc.invalidateQueries({ queryKey: ["monitoring"] });
  };

  const upsert = useMutation({
    mutationFn: async ({ cam, plan }: { cam: Camera; plan?: FisheyePlan | null }) => {
      const written = cam.created_at ? await api.cameras.update(cam) : await api.cameras.upsert(cam);
      if (!plan) return written;
      const id = written.camera?.id ?? cam.id;
      let last = written;
      if (plan.lens === null) {
        for (const viewId of plan.removeViewIds) await api.cameras.deleteView(id, viewId);
        last = await api.cameras.setLens(id, null);
      } else {
        last = await api.cameras.setLens(id, plan.lens);
        for (const viewId of plan.removeViewIds) last = await api.cameras.deleteView(id, viewId);
        for (const view of plan.addViews) last = await api.cameras.addView(id, view);
        // A fisheye is calibrated on a picture of it, so go straight there.
        if (!isCalibrated(plan.lens)) setFisheye({ id });
      }
      return { ...last, apply_errors: [...written.apply_errors, ...last.apply_errors] };
    },
    onSuccess: onWrote,
    onError: (e: unknown) =>
      setWriteError(
        e instanceof ApiError ? e.message : "Could not save the camera.",
      ),
  });
  const del = useMutation({ mutationFn: api.cameras.delete, onSuccess: onWrote });
  const resync = useMutation({ mutationFn: () => api.cameras.apply(), onSuccess: onWrote });

  const nvrById = useMemo(() => {
    const map = new Map<string, Nvr>();
    (nvrs.data ?? []).forEach((n) => map.set(n.id, n));
    return map;
  }, [nvrs.data]);

  const list = cams.data ?? [];
  const visible = useMemo(
    () =>
      searchItems(list, query, (c) => [
        c.name,
        c.id,
        c.store,
        c.rtsp_url,
        c.nvr_id ?? undefined,
      ]),
    [list, query],
  );

  const storeNames = stores.map((s) => s.name);
  const defaultStore = scope.kind === "store" ? scope.store : "";
  const cap = capacity.data;

  // The AI slot check has to look at the WHOLE fleet, not the scoped view: the
  // limit is fleet-wide, so a camera hidden by the current scope still uses one.
  const aiRemaining = (draftId: string): { fleet: number; store: number } => {
    const everything = allCams.data ?? list;
    const aiOthers = everything.filter(
      (c) => c.id !== draftId && (c.role ?? "ai") !== "view_only",
    );
    return {
      fleet: cap?.ai_max == null ? Infinity : cap.ai_max - aiOthers.length,
      store: 0, // filled per-store in the modal, which knows the chosen store
    };
  };

  return (
    <SettingsSection
      title="Cameras"
      description={`Cameras in ${scopeLabel.toLowerCase() === "all stores" ? "all stores" : `“${scopeLabel}”`}.`}
      hint={
        <>
          Adding, editing or removing a camera applies to live capture
          immediately — the pipeline keeps running and other cameras are not
          interrupted. Use <b>Sync pipeline</b> only to retry after a failure.
        </>
      }
      actions={
        canEdit && (
          <>
            <button
              className="bf-button bg-white/10 bg-none disabled:opacity-40"
              onClick={() => resync.mutate()}
              disabled={resync.isPending}
              title="Re-sync the live pipeline against the saved cameras. Changes normally apply on save; this is for retrying after a failure."
            >
              <RefreshCw
                size={16}
                className={"mr-2 " + (resync.isPending ? "animate-spin" : "")}
              />
              {resync.isPending ? "Syncing…" : "Sync pipeline"}
            </button>
            <button
              className="bf-button bg-white/10 bg-none disabled:opacity-40"
              onClick={() => setBulk(true)}
              disabled={storeNames.length === 0}
              title={storeNames.length === 0 ? "Create a store first" : undefined}
            >
              <Plus size={16} className="mr-2" /> Bulk add
            </button>
            <button
              className="bf-button disabled:opacity-40"
              onClick={() =>
                setDraft({
                  ...emptyCamera,
                  store: defaultStore || storeNames[0] || "",
                })
              }
              disabled={storeNames.length === 0}
              title={storeNames.length === 0 ? "Create a store first" : undefined}
            >
              <Plus size={16} className="mr-2" /> Add camera
            </button>
          </>
        )
      }
    >
      {cap && <CapacityBadges capacity={cap} scopeStore={defaultStore} />}

      {cap && Object.keys(cap.over_limit ?? {}).length > 0 && (
        <Note tone="warning">
          {Object.keys(cap.over_limit ?? {}).length} AI camera
          {Object.keys(cap.over_limit ?? {}).length === 1 ? " is" : "s are"} configured
          but not being analysed because the fleet is over its limits. They are
          marked below. Raise the limits in Capacity &amp; limits, remove some AI
          cameras, or switch them to Non-AI (which is unlimited and still gives
          live viewing).
        </Note>
      )}

      {canEdit && storeNames.length === 0 && (
        <Note tone="warning">
          Add a store first — every camera and recorder belongs to one.
        </Note>
      )}

      {writeError && <Note tone="error">{writeError}</Note>}

      {lastWrite &&
        (lastWrite.apply_errors.length > 0 ? (
          <Note tone="warning">
            Saved, but not everything went live:{" "}
            {humanizeApplyErrors(lastWrite.apply_errors, role)}. The system keeps
            retrying; use <span className="text-bf-text">Sync pipeline</span> to
            retry now.
          </Note>
        ) : (
          <Note tone="success">Changes are live.</Note>
        ))}

      {list.length > 6 && (
        <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-bf-surface/60 px-3 py-2">
          <Search size={15} className="shrink-0 text-bf-muted" />
          <input
            className="w-full bg-transparent text-sm text-bf-text outline-none placeholder:text-bf-muted"
            placeholder="Search cameras by name, id, store or URL…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search cameras"
          />
        </label>
      )}

      {cams.isLoading ? (
        <div className="bf-card text-sm text-bf-muted">Loading cameras…</div>
      ) : list.length === 0 ? (
        <EmptyState
          icon={CamIcon}
          title={
            scope.kind === "all"
              ? "No cameras yet"
              : `No cameras in “${scopeLabel}”`
          }
          hint={
            storeNames.length === 0
              ? "Create a store first, then add the cameras that belong to it."
              : "Add a camera directly by its stream URL, or by channel from a recorder."
          }
        />
      ) : visible.length === 0 ? (
        <EmptyState title="No camera matches that search." />
      ) : (
        <div className="bf-card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-bf-muted">
              <tr>
                <th className="p-3">Name</th>
                <th className="p-3">Source</th>
                <th className="p-3">Store</th>
                <th className="p-3">Max resolution</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {visible.map((c) => (
                <tr key={c.id} className="border-t border-white/5">
                  <td className="p-3">
                    <div className="flex items-center gap-2 font-medium">
                      {c.name || c.id}
                      {(c.role ?? "ai") === "view_only" ? (
                        <span
                          className="rounded bg-white/10 px-1.5 py-0.5 text-xs uppercase tracking-wide text-bf-muted"
                          title="Non-AI camera: live view only, no detection, search or alerts"
                        >
                          non-AI
                        </span>
                      ) : (
                        <span
                          className="rounded bg-bf-magenta/15 px-1.5 py-0.5 text-xs uppercase tracking-wide text-bf-magenta ring-1 ring-inset ring-bf-magenta/40"
                          title="AI camera: analysed for detection, search and alerts"
                        >
                          AI
                        </span>
                      )}
                      {c.parent_id && (
                        <span
                          className="rounded bg-white/10 px-1.5 py-0.5 text-xs uppercase tracking-wide text-bf-muted"
                          title="A dewarped view of a fisheye camera, analysed as its own AI camera"
                        >
                          fisheye view
                        </span>
                      )}
                      {c.lens && (
                        <span
                          className="rounded bg-white/10 px-1.5 py-0.5 text-xs uppercase tracking-wide text-bf-muted"
                          title="Fisheye lens: dewarped look-around in Monitoring"
                        >
                          fisheye
                        </span>
                      )}
                      {cap?.over_limit?.[c.id] && (
                        <span
                          className="rounded bg-amber-400/10 px-1.5 py-0.5 text-xs uppercase tracking-wide text-amber-300 ring-1 ring-inset ring-amber-400/30"
                          title={cap.over_limit[c.id]}
                        >
                          over limit
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-xs">
                    {c.nvr_id ? (
                      <>
                        <div>
                          Recorder{" "}
                          <span className="text-bf-text">
                            {nvrById.get(c.nvr_id)?.name || c.nvr_id}
                          </span>
                        </div>
                        <div className="text-bf-muted">
                          Channel {c.channel}
                          {c.sub_rtsp_url ? " · main + sub" : " · main only"}
                        </div>
                      </>
                    ) : (
                      <span className="break-all font-mono text-bf-muted">
                        {c.rtsp_url}
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {c.store ? (
                      <span className="rounded-full bg-bf-violet/20 px-2 py-0.5 text-xs text-bf-text">
                        {c.store}
                      </span>
                    ) : (
                      <span className="text-xs text-bf-muted">—</span>
                    )}
                  </td>
                  <td className="p-3 text-xs text-bf-muted">
                    {c.max_height ? `${c.max_height}p` : "No limit"}
                  </td>
                  <td className="p-3 text-right">
                    {canEdit && (
                      <div className="flex justify-end gap-3">
                        {!c.parent_id && (
                          <button
                            className={c.lens ? "text-bf-magenta hover:text-bf-text" : "text-bf-muted hover:text-bf-text"}
                            onClick={() => setFisheye({ id: c.id })}
                            aria-label={`Fisheye settings for ${c.id}`}
                            title="Fisheye lens and AI views"
                          >
                            <ScanEye size={15} />
                          </button>
                        )}
                        <button
                          className="text-bf-muted hover:text-bf-text"
                          onClick={() =>
                            c.parent_id
                              ? setFisheye({ id: c.parent_id, focus: c.id })
                              : setDraft(c)
                          }
                          aria-label={`Edit ${c.id}`}
                        >
                          <Pencil size={15} />
                        </button>
                        <button
                          className="text-bf-muted hover:text-red-400"
                          onClick={() => del.mutate(c.id)}
                          aria-label={`Delete ${c.id}`}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {draft && (
        <CameraDraftModal
          draft={draft}
          setDraft={setDraft}
          nvrs={nvrs.data ?? []}
          stores={stores}
          capacity={cap}
          allCameras={allCams.data ?? list}
          fleetRemaining={aiRemaining(draft.id).fleet}
          onSubmit={(c, plan) => {
            upsert.mutate({ cam: c, plan });
            setDraft(null);
          }}
        />
      )}

      {fisheye &&
        (() => {
          const everything = allCams.data ?? list;
          const parent = everything.find((c) => c.id === fisheye.id);
          if (!parent) return null;
          return (
            <FisheyeDialog
              camera={parent}
              views={everything.filter((c) => c.parent_id === parent.id)}
              focusViewId={fisheye.focus}
              onClose={() => setFisheye(null)}
            />
          );
        })()}

      {bulk && (
        <BulkAddModal
          nvrs={nvrs.data ?? []}
          stores={stores}
          defaultStore={defaultStore}
          onClose={() => setBulk(false)}
          onSubmit={(list2) => {
            list2.forEach((c) => upsert.mutate({ cam: c }));
            setBulk(false);
          }}
        />
      )}
    </SettingsSection>
  );
}

function CapacityBadges({
  capacity,
  scopeStore,
}: {
  capacity: CameraCapacity;
  scopeStore: string;
}) {
  const perStoreUsed = scopeStore ? (capacity.ai_per_store?.[scopeStore] ?? 0) : null;
  const full = capacity.ai_max != null && capacity.ai_used >= capacity.ai_max;
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs">
      <span
        className={
          "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 font-medium ring-1 ring-inset " +
          (full
            ? "bg-amber-500/10 text-amber-200 ring-amber-500/30"
            : "bg-bf-magenta/10 text-bf-magenta ring-bf-magenta/30")
        }
      >
        AI cameras {capacity.ai_used}/{capacity.ai_max ?? "∞"}
        <InfoHint>
          AI cameras are analysed for detection, search and alerts. The
          detection engine has a fixed number of slots shared by every store,
          plus a per-store allowance. Both are set in Capacity &amp; limits.
        </InfoHint>
      </span>
      {perStoreUsed !== null && capacity.ai_max_per_store != null && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 font-medium text-bf-muted ring-1 ring-inset ring-white/10">
          {perStoreUsed}/{capacity.ai_max_per_store} in this store
        </span>
      )}
      <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 font-medium text-bf-muted ring-1 ring-inset ring-white/10">
        Non-AI {capacity.view_only_used}
        <InfoHint>
          Non-AI cameras are relayed for live viewing only. They use no
          detection capacity and no GPU at all until somebody watches them, so
          there is no limit on how many you add.
        </InfoHint>
      </span>
    </div>
  );
}

/** What saving a camera does to its fisheye setup, applied after the camera
 *  row itself is written. */
export type FisheyePlan = {
  lens: Lens | null;
  addViews: Array<Pose & { name: string }>;
  removeViewIds: string[];
};

function CameraDraftModal({
  draft,
  setDraft,
  nvrs,
  stores,
  capacity,
  allCameras,
  fleetRemaining,
  onSubmit,
}: {
  draft: Camera;
  setDraft: (c: Camera | null) => void;
  nvrs: Nvr[];
  stores: Store[];
  capacity?: CameraCapacity;
  allCameras: Camera[];
  fleetRemaining: number;
  onSubmit: (c: Camera, plan: FisheyePlan | null) => void;
}) {
  const initialKind: "nvr" | "direct" = draft.nvr_id ? "nvr" : "direct";
  const [kind, setKind] = useState<"nvr" | "direct">(initialKind);
  const initialCreds = splitRtspCredentials(draft.rtsp_url || "");
  const [directUrl, setDirectUrl] = useState(initialCreds.base);
  const [directUser, setDirectUser] = useState(initialCreds.username);
  const [directPass, setDirectPass] = useState(initialCreds.password);
  const [subUrl, setSubUrl] = useState(draft.sub_rtsp_url ?? "");
  const [dewarpedText, setDewarpedText] = useState(
    (draft.dewarped_channels ?? []).join(", "),
  );

  // Fisheye: the disk itself is relayed for viewing; AI runs on dewarped views
  // of it, each its own AI camera.
  const ownViews = allCameras.filter((c) => c.parent_id && c.parent_id === draft.id);
  const [fisheye, setFisheye] = useState(Boolean(draft.lens));
  const [mount, setMount] = useState<Mount>(draft.lens?.mount ?? "ceiling");
  const [fisheyeAi, setFisheyeAi] = useState(ownViews.length > 0 || (!draft.created_at && (draft.role ?? "ai") === "ai"));
  const presetViews = VIEW_PRESETS.flatMap((p) =>
    p.views({ mount }).map((v) => ({ ...v, key: `${p.id}:${v.name}`, preset: p.label })),
  );
  const [pickedViews, setPickedViews] = useState<Set<string>>(
    () =>
      new Set(
        presetViews
          .filter((p) => ownViews.some((v) => v.view && samePose(v.view, p)))
          .map((p) => p.key),
      ),
  );
  const customViews = ownViews.filter(
    (v) => !v.view || !presetViews.some((p) => samePose(p, v.view!)),
  );
  const [pendingCustom, setPendingCustom] = useState<DraftCustomView[]>([]);
  const [removedCustom, setRemovedCustom] = useState<Set<string>>(() => new Set());
  const keptCustomCount = customViews.filter((v) => !removedCustom.has(v.id)).length;
  const wantedViewCount =
    fisheye && fisheyeAi ? pickedViews.size + keptCustomCount + pendingCustom.length : 0;
  const canFrame = Boolean(draft.created_at) && isCalibrated(draft.lens);

  const chosenStore = (draft.store || "").trim();
  const storeAiUsed = allCameras.filter(
    (c) =>
      c.id !== draft.id &&
      c.parent_id !== draft.id &&
      (c.role ?? "ai") !== "view_only" &&
      (c.store ?? "") === chosenStore,
  ).length;
  const storeMax = capacity?.ai_max_per_store ?? null;
  const storeFull = storeMax != null && storeAiUsed >= storeMax;
  const fleetFull = fleetRemaining + ownViews.length <= 0;
  const aiBlocked = (fleetFull || storeFull) && (draft.role ?? "ai") !== "ai";
  const viewSlots = Math.min(
    storeMax == null ? Infinity : storeMax - storeAiUsed,
    fleetRemaining + ownViews.length,
  );
  const viewsOverLimit = wantedViewCount > viewSlots;

  const selectedNvr = nvrs.find((n) => n.id === draft.nvr_id);
  const nvrHasSub =
    selectedNvr != null &&
    (selectedNvr.supports_sub_stream ??
      BRANDS_WITH_SUB_STREAM.has(selectedNvr.brand));

  return (
    <Modal
      onClose={() => setDraft(null)}
      labelledBy="camera-draft-title"
      panelClassName={fisheye ? "w-full max-w-2xl" : "w-full max-w-lg"}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          const cleaned: Camera = {
            ...draft,
            nvr_id: kind === "nvr" ? draft.nvr_id || null : null,
            channel: kind === "nvr" ? (draft.channel ?? null) : null,
            rtsp_url:
              kind === "direct"
                ? applyRtspCredentials(directUrl, directUser.trim(), directPass)
                : "",
            // The recorder's template supplies the sub URL; only a direct
            // camera needs one typed in.
            sub_rtsp_url: kind === "direct" ? subUrl.trim() : "",
            store: chosenStore,
            // The fisheye disk itself is never analysed; its views are.
            role: fisheye ? "view_only" : draft.role,
            dewarped_channels:
              kind === "nvr" && fisheye && dewarpedText.trim()
                ? parseChannelList(dewarpedText, draft.channel)
                : [],
          };
          if (!cleaned.created_at) cleaned.id = deriveCameraId(cleaned);
          let plan: FisheyePlan | null = null;
          if (fisheye) {
            const wanted = fisheyeAi ? presetViews.filter((p) => pickedViews.has(p.key)) : [];
            plan = {
              lens: { ...(draft.lens ?? DEFAULT_LENS), mount },
              addViews: [
                ...wanted
                  .filter((p) => !ownViews.some((v) => v.view && samePose(v.view, p)))
                  .map(({ name, yaw, tilt, fov }) => ({ name, yaw, tilt, fov })),
                ...(fisheyeAi ? pendingCustom : []),
              ],
              removeViewIds: ownViews
                .filter(
                  (v) =>
                    !fisheyeAi ||
                    removedCustom.has(v.id) ||
                    (v.view &&
                      presetViews.some((p) => samePose(p, v.view!)) &&
                      !wanted.some((p) => samePose(p, v.view!))),
                )
                .map((v) => v.id),
            };
          } else if (draft.lens) {
            plan = { lens: null, addViews: [], removeViewIds: ownViews.map((v) => v.id) };
          }
          onSubmit(cleaned, plan);
        }}
        className="space-y-4"
      >
        <h3 id="camera-draft-title" className="text-lg font-medium text-white">
          {draft.created_at ? "Edit camera" : "Add a camera"}
        </h3>

        <div className="block">
          <FieldLabel htmlFor="cam-name">Display name</FieldLabel>
          <input
            id="cam-name"
            className="bf-input"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder="Front entrance"
          />
        </div>

        <div>
          <FieldLabel
            hint={
              <>
                A camera can come from a recorder (give it the channel number
                and the stream URLs are built for you) or straight from its own
                RTSP address.
              </>
            }
          >
            Source
          </FieldLabel>
          <div className="flex gap-2">
            <TabButton active={kind === "nvr"} onClick={() => setKind("nvr")}>
              From a recorder
            </TabButton>
            <TabButton active={kind === "direct"} onClick={() => setKind("direct")}>
              Direct camera URL
            </TabButton>
          </div>
        </div>

        {kind === "nvr" ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="mb-1 block text-xs text-bf-muted">Recorder</span>
                <SearchableSelect
                  ariaLabel="Recorder"
                  value={draft.nvr_id ?? ""}
                  onChange={(v) => setDraft({ ...draft, nvr_id: v || null })}
                  placeholder="Choose…"
                  options={nvrs.map((n) => ({
                    value: n.id,
                    label: n.name || n.id,
                    hint: `${n.host} · ${n.brand}`,
                    keywords: n.store,
                  }))}
                />
                {nvrs.length === 0 && (
                  <span className="text-xs text-amber-300">
                    No recorders yet. Add one in the Recorders section.
                  </span>
                )}
              </div>
              <label>
                <span className="mb-1 block text-xs text-bf-muted">Channel #</span>
                <input
                  className="bf-input"
                  type="number"
                  min={1}
                  value={draft.channel ?? ""}
                  onChange={(e) =>
                    setDraft({
                      ...draft,
                      channel:
                        e.target.value === "" ? null : Number(e.target.value),
                    })
                  }
                  required
                  placeholder="1"
                />
              </label>
            </div>
            <p className="text-xs text-bf-muted">
              {nvrHasSub
                ? "Main and sub streams are both set up automatically."
                : selectedNvr
                  ? "This recorder's brand has no known sub stream, so main only."
                  : "Stream URLs are derived from the recorder."}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="block">
              <FieldLabel htmlFor="cam-url">Camera URL</FieldLabel>
              <input
                id="cam-url"
                className="bf-input font-mono text-xs"
                value={directUrl}
                onChange={(e) => {
                  const parts = splitRtspCredentials(e.target.value);
                  setDirectUrl(parts.base);
                  if (parts.username) setDirectUser(parts.username);
                  if (parts.password) setDirectPass(parts.password);
                }}
                placeholder="rtsp://10.0.0.50:554/Streaming/Channels/101"
                required
              />
            </div>
            <div className="block">
              <FieldLabel
                htmlFor="cam-sub-url"
                hint="The camera's second, lower-bitrate stream, if it has one. When set, viewers can choose Main or Sub while watching the original stream."
              >
                Sub-stream URL (optional)
              </FieldLabel>
              <input
                id="cam-sub-url"
                className="bf-input font-mono text-xs"
                value={subUrl}
                onChange={(e) => setSubUrl(e.target.value)}
                placeholder="rtsp://10.0.0.50:554/Streaming/Channels/102"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <FieldLabel
                  htmlFor="cam-user"
                  hint={
                    <>
                      Put credentials here rather than inside the URL — they are
                      encoded when the stream is opened, so passwords containing{" "}
                      <code className="font-mono">@ : / ? #</code> work.
                    </>
                  }
                >
                  Username
                </FieldLabel>
                <input
                  id="cam-user"
                  className="bf-input"
                  value={directUser}
                  onChange={(e) => setDirectUser(e.target.value)}
                  placeholder="admin"
                  autoComplete="off"
                />
              </div>
              <div>
                <FieldLabel htmlFor="cam-pass">Password</FieldLabel>
                <input
                  id="cam-pass"
                  className="bf-input"
                  type="password"
                  value={directPass}
                  onChange={(e) => setDirectPass(e.target.value)}
                  autoComplete="new-password"
                />
              </div>
            </div>
          </div>
        )}

        <div className="space-y-3">
          <FieldLabel
            hint={
              <>
                A <b>fisheye</b> camera sees the whole room in one round picture.
                It is shown dewarped, with look-around, in live view, playback and
                clips, and AI analyses the views of it you choose.
              </>
            }
          >
            Lens
          </FieldLabel>
          <div className="flex gap-2">
            <TabButton active={!fisheye} onClick={() => setFisheye(false)}>
              Standard
            </TabButton>
            <TabButton active={fisheye} onClick={() => setFisheye(true)}>
              Fisheye
            </TabButton>
          </div>
          {fisheye && (
            <>
              <div>
                <span className="mb-1 block text-xs text-bf-muted">Mounted on</span>
                <div className="flex gap-2">
                  {(Object.keys(MOUNT_LABEL) as Mount[]).map((m) => (
                    <TabButton key={m} active={mount === m} onClick={() => setMount(m)}>
                      {MOUNT_LABEL[m]}
                    </TabButton>
                  ))}
                </div>
              </div>
              {kind === "nvr" && (
                <div>
                  <FieldLabel
                    htmlFor="cam-dewarped"
                    hint="Channels where the recorder publishes the camera's own PTZ or panorama views, if it is set up that way. They are added as Non-AI cameras in this store, main stream only."
                  >
                    Recorder view channels (optional)
                  </FieldLabel>
                  <input
                    id="cam-dewarped"
                    className="bf-input font-mono text-xs"
                    value={dewarpedText}
                    onChange={(e) => setDewarpedText(e.target.value)}
                    placeholder="11, 12, 13, 14"
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <FieldLabel>Store</FieldLabel>
            <SearchableSelect
              ariaLabel="Store"
              value={chosenStore}
              onChange={(v) => setDraft({ ...draft, store: v })}
              placeholder="Choose a store…"
              options={stores.map((s) => ({
                value: s.name,
                label: s.display_name || s.name,
                hint: s.location || undefined,
                badge: s.is_simulation ? "Sim" : undefined,
                keywords: s.name,
              }))}
            />
          </div>
          <div>
            <FieldLabel hint="The tallest picture this camera is allowed to send. If it streams anything taller, it is refused with an explanation rather than quietly using up decode capacity — either lower it at the camera, or raise this limit.">
              Maximum allowed resolution
            </FieldLabel>
            <SearchableSelect
              ariaLabel="Maximum allowed resolution"
              value={String(draft.max_height ?? 0)}
              onChange={(v) => setDraft({ ...draft, max_height: Number(v) })}
              searchThreshold={99}
              options={MAX_HEIGHT_OPTIONS.map((o) => ({
                value: String(o.value),
                label: o.label,
              }))}
            />
          </div>
          {(draft.role ?? "ai") === "ai" && (
            <div>
              <FieldLabel hint="How many frames per second are analysed for detection and tracking. Live view always shows the camera's full frame rate. Fast scenes (doors, tills, vehicles) need more; quiet aisles need fewer, and fewer leaves room for more cameras.">
                Analysis frame rate
              </FieldLabel>
              <SearchableSelect
                ariaLabel="Analysis frame rate"
                value={String(draft.ingest_max_fps ?? 0)}
                onChange={(v) => setDraft({ ...draft, ingest_max_fps: Number(v) })}
                searchThreshold={99}
                options={INGEST_FPS_OPTIONS.map((o) => ({
                  value: String(o.value),
                  label: o.label,
                }))}
              />
            </div>
          )}
        </div>

        <div>
          <FieldLabel
            hint={
              <>
                <b>AI cameras</b> are analysed for detection, search and alerts,
                and use one of the limited detection slots. <b>Non-AI cameras</b>{" "}
                are relayed for live viewing only — unlimited, and costing
                nothing until somebody watches them.
              </>
            }
          >
            Camera type
          </FieldLabel>
          {fisheye ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <TabButton active={fisheyeAi} onClick={() => setFisheyeAi(true)} accent="magenta">
                  AI on dewarped views
                </TabButton>
                <TabButton active={!fisheyeAi} onClick={() => setFisheyeAi(false)}>
                  Non-AI camera
                </TabButton>
              </div>
              {fisheyeAi && (
                <div className="space-y-2">
                  {VIEW_PRESETS.map((p) => (
                    <div key={p.id}>
                      <span className="mb-1 block text-xs text-bf-muted">{p.label}</span>
                      <div className="flex flex-wrap gap-1">
                        {presetViews
                          .filter((v) => v.preset === p.label)
                          .map((v) => {
                            const on = pickedViews.has(v.key);
                            return (
                              <button
                                key={v.key}
                                type="button"
                                aria-pressed={on}
                                className={`rounded-md border px-2 py-1 text-xs ${
                                  on
                                    ? "border-bf-magenta/60 bg-bf-magenta/15 text-white"
                                    : "border-white/10 text-bf-muted hover:text-white"
                                }`}
                                onClick={() => {
                                  const next = new Set(pickedViews);
                                  if (on) next.delete(v.key);
                                  else next.add(v.key);
                                  setPickedViews(next);
                                }}
                              >
                                {v.name}
                              </button>
                            );
                          })}
                      </div>
                    </div>
                  ))}
                  {canFrame && isCalibrated(draft.lens) ? (
                    <FisheyeCustomViews
                      camera={draft}
                      lens={draft.lens}
                      existing={customViews}
                      removed={removedCustom}
                      onToggleRemove={(id) => {
                        const next = new Set(removedCustom);
                        if (next.has(id)) next.delete(id);
                        else next.add(id);
                        setRemovedCustom(next);
                      }}
                      pending={pendingCustom}
                      onAdd={(v) => setPendingCustom([...pendingCustom, v])}
                      onRemovePending={(i) => setPendingCustom(pendingCustom.filter((_, j) => j !== i))}
                    />
                  ) : (
                    <p className="text-xs text-bf-muted">
                      Custom views are framed in a free look-around of the camera once its lens
                      is calibrated; the fisheye editor opens for that when the camera is saved.
                    </p>
                  )}
                  <p className="text-xs text-bf-muted">Each view is analysed as its own AI camera.</p>
                  {viewsOverLimit && (
                    <span className="block text-xs text-amber-300">
                      {wantedViewCount} AI views, but only {Math.max(0, viewSlots)} AI camera
                      slot{viewSlots === 1 ? "" : "s"} left for this store. Pick fewer views, or ask a
                      dev user to raise the limit in Capacity &amp; limits.
                    </span>
                  )}
                </div>
              )}
            </div>
          ) : (
            <div className="flex gap-2">
              <TabButton
                active={(draft.role ?? "ai") === "ai"}
                onClick={() => setDraft({ ...draft, role: "ai" })}
                disabled={aiBlocked}
                accent="magenta"
                title={
                  aiBlocked
                    ? "No AI slots left. Free one, raise the limit, or add this camera as Non-AI."
                    : undefined
                }
              >
                AI camera
              </TabButton>
              <TabButton
                active={draft.role === "view_only"}
                onClick={() => setDraft({ ...draft, role: "view_only" })}
              >
                Non-AI camera
              </TabButton>
            </div>
          )}
          {!fisheye && aiBlocked && (
            <span className="mt-1 block text-xs text-amber-300">
              {fleetFull
                ? `All AI camera slots are in use across the system${capacity?.ai_max != null ? ` (${capacity.ai_used}/${capacity.ai_max})` : ""}.`
                : `This store has used its AI camera allowance (${storeAiUsed}/${storeMax}).`}{" "}
              Add this camera as Non-AI, or ask a dev user to raise the limit in
              Capacity &amp; limits.
            </span>
          )}
        </div>

        {!fisheye && (draft.role ?? "ai") === "ai" && (
          <div className="space-y-3">
            <FieldLabel hint="Follows the Descriptions settings unless this camera sets its own.">
              Descriptions
            </FieldLabel>
            <TriStateRow
              label="People description"
              value={draft.people_description}
              onChange={(people_description) =>
                setDraft({ ...draft, people_description })
              }
            />
            <TriStateRow
              label="Activity description"
              value={draft.activity_description}
              onChange={(activity_description) =>
                setDraft({ ...draft, activity_description })
              }
            />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button
            type="button"
            className="bf-button bg-white/10 bg-none"
            onClick={() => setDraft(null)}
          >
            Cancel
          </button>
          <button
            className="bf-button disabled:opacity-40"
            type="submit"
            disabled={!chosenStore || viewsOverLimit}
          >
            Save camera
          </button>
        </div>
      </form>
    </Modal>
  );
}

type TriFlag = boolean | null | undefined;

function TriStateRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: TriFlag;
  onChange: (v: boolean | null) => void;
}) {
  const current = value == null ? "inherit" : value ? "on" : "off";
  const options = [
    { id: "inherit" as const, text: "Same as system", send: null },
    { id: "on" as const, text: "On", send: true },
    { id: "off" as const, text: "Off", send: false },
  ];
  return (
    <div>
      <span className="mb-1 block text-xs text-bf-muted">{label}</span>
      <div role="radiogroup" aria-label={label} className="flex gap-2">
        {options.map((opt) => {
          const selected = current === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => onChange(opt.send)}
              className={
                "flex-1 rounded-md border px-3 py-2 text-sm transition " +
                (selected
                  ? "border-bf-violet bg-bf-violet/10 text-bf-text"
                  : "border-white/10 text-bf-muted hover:text-bf-text")
              }
            >
              {opt.text}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function TabButton({
  active,
  onClick,
  children,
  disabled,
  title,
  accent = "violet",
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  disabled?: boolean;
  title?: string;
  accent?: "violet" | "magenta";
}) {
  const on =
    accent === "magenta"
      ? "border-bf-magenta bg-bf-magenta/10 text-bf-text"
      : "border-bf-violet bg-bf-violet/10 text-bf-text";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={
        "flex-1 rounded-md border px-3 py-2 text-sm transition disabled:cursor-not-allowed disabled:opacity-50 " +
        (active ? on : "border-white/10 text-bf-muted hover:text-bf-text")
      }
    >
      {children}
    </button>
  );
}

function BulkAddModal({
  nvrs,
  stores,
  defaultStore,
  onClose,
  onSubmit,
}: {
  nvrs: Nvr[];
  stores: Store[];
  defaultStore: string;
  onClose: () => void;
  onSubmit: (cams: Camera[]) => void;
}) {
  const [kind, setKind] = useState<"direct" | "nvr">(
    nvrs.length > 0 ? "nvr" : "direct",
  );
  const [firstUrl, setFirstUrl] = useState("");
  const [nvrId, setNvrId] = useState("");
  const [startChannel, setStartChannel] = useState(1);
  const [count, setCount] = useState(4);
  const [store, setStore] = useState(defaultStore || stores[0]?.name || "");
  const [namePrefix, setNamePrefix] = useState("");
  const [role, setRole] = useState<"ai" | "view_only">("view_only");
  const [maxHeight, setMaxHeight] = useState(0);

  const directPreview = useMemo(() => {
    if (kind !== "direct" || !firstUrl.trim() || count < 1) return [];
    return buildBulkUrls(firstUrl, Math.min(count, 64));
  }, [kind, firstUrl, count]);

  const channelPreview = useMemo(() => {
    if (kind !== "nvr" || !nvrId || count < 1) return [];
    const start = Number.isFinite(startChannel) ? startChannel : 1;
    return Array.from({ length: Math.min(count, 64) }, (_, i) => start + i);
  }, [kind, nvrId, startChannel, count]);

  const invalidDirect =
    kind === "direct" && firstUrl.trim().length > 0 && directPreview.length === 0;
  const previewCount =
    kind === "direct" ? directPreview.length : channelPreview.length;

  const submit = () => {
    const base = { ...emptyCamera, store: store.trim(), role, max_height: maxHeight };
    const cams: Camera[] =
      kind === "direct"
        ? directPreview.map((url, i) => {
            const cam: Camera = {
              ...base,
              rtsp_url: url,
              name: namePrefix.trim() ? `${namePrefix.trim()} ${i + 1}` : "",
            };
            cam.id = deriveCameraId(cam);
            return cam;
          })
        : channelPreview.map((ch, i) => {
            const cam: Camera = {
              ...base,
              nvr_id: nvrId,
              channel: ch,
              rtsp_url: "",
              name: namePrefix.trim() ? `${namePrefix.trim()} ${i + 1}` : "",
            };
            cam.id = deriveCameraId(cam);
            return cam;
          });
    onSubmit(cams);
  };

  return (
    <Modal onClose={onClose} labelledBy="bulk-add-title" panelClassName="w-full max-w-lg">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="space-y-4"
      >
        <h3 id="bulk-add-title" className="text-lg font-medium text-white">
          Bulk add cameras
        </h3>

        <div>
          <span className="mb-1 block text-xs text-bf-muted">Source</span>
          <div className="flex gap-2">
            <TabButton active={kind === "nvr"} onClick={() => setKind("nvr")}>
              Recorder channels
            </TabButton>
            <TabButton active={kind === "direct"} onClick={() => setKind("direct")}>
              Direct camera URLs
            </TabButton>
          </div>
        </div>

        {kind === "direct" ? (
          <>
            <p className="text-xs text-bf-muted">
              Paste the first camera's URL and a count. The trailing number is
              incremented (zero-padding preserved) to create the rest.
            </p>
            <label className="block">
              <span className="mb-1 block text-xs text-bf-muted">
                First camera URL
              </span>
              <input
                className="bf-input font-mono text-xs"
                value={firstUrl}
                onChange={(e) => setFirstUrl(e.target.value)}
                placeholder="rtsp://user:pass@10.0.0.50:554/cam01"
                required
              />
              {invalidDirect && (
                <span className="text-xs text-amber-300">
                  No trailing number found in the URL to increment.
                </span>
              )}
            </label>
          </>
        ) : (
          <>
            <p className="text-xs text-bf-muted">
              Pick a recorder and a starting channel. One camera per channel; the
              stream URLs come from the recorder's brand template.
            </p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <span className="mb-1 block text-xs text-bf-muted">Recorder</span>
                <SearchableSelect
                  ariaLabel="Recorder"
                  value={nvrId}
                  onChange={(id) => {
                    setNvrId(id);
                    const n = nvrs.find((x) => x.id === id);
                    if (n?.store && !store.trim()) setStore(n.store);
                  }}
                  placeholder="Choose…"
                  options={nvrs.map((n) => ({
                    value: n.id,
                    label: n.name || n.id,
                    hint: `${n.host} · ${n.brand}`,
                  }))}
                />
                {nvrs.length === 0 && (
                  <span className="text-xs text-amber-300">
                    No recorders yet.
                  </span>
                )}
              </div>
              <label className="block">
                <span className="mb-1 block text-xs text-bf-muted">
                  Start channel #
                </span>
                <input
                  className="bf-input"
                  type="number"
                  min={1}
                  value={startChannel}
                  onChange={(e) => setStartChannel(Number(e.target.value))}
                  required
                />
              </label>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="mb-1 block text-xs text-bf-muted">
              Number of cameras
            </span>
            <input
              className="bf-input"
              type="number"
              min={1}
              max={64}
              value={count}
              onChange={(e) => setCount(Number(e.target.value))}
              required
            />
          </label>
          <div className="block">
            <span className="mb-1 block text-xs text-bf-muted">Store</span>
            <SearchableSelect
              ariaLabel="Store"
              value={store}
              onChange={setStore}
              placeholder="Choose a store…"
              options={stores.map((s) => ({
                value: s.name,
                label: s.display_name || s.name,
                hint: s.location || undefined,
                badge: s.is_simulation ? "Sim" : undefined,
              }))}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <span className="mb-1 block text-xs text-bf-muted">Camera type</span>
            <div className="flex gap-2">
              <TabButton
                active={role === "ai"}
                onClick={() => setRole("ai")}
                accent="magenta"
              >
                AI
              </TabButton>
              <TabButton
                active={role === "view_only"}
                onClick={() => setRole("view_only")}
              >
                Non-AI
              </TabButton>
            </div>
            <span className="mt-1 block text-xs text-bf-muted">
              Bulk adds default to Non-AI: AI slots are limited, and a batch is
              the easiest way to exhaust them by accident.
            </span>
          </div>
          <div>
            <span className="mb-1 block text-xs text-bf-muted">
              Maximum allowed resolution
            </span>
            <SearchableSelect
              ariaLabel="Maximum allowed resolution"
              value={String(maxHeight)}
              onChange={(v) => setMaxHeight(Number(v))}
              searchThreshold={99}
              options={MAX_HEIGHT_OPTIONS.map((o) => ({
                value: String(o.value),
                label: o.label,
              }))}
            />
          </div>
        </div>

        <label className="block">
          <span className="mb-1 block text-xs text-bf-muted">
            Name prefix (optional)
          </span>
          <input
            className="bf-input"
            value={namePrefix}
            onChange={(e) => setNamePrefix(e.target.value)}
            placeholder='e.g. "Aisle" → "Aisle 1", "Aisle 2", …'
          />
        </label>

        {previewCount > 0 && (
          <div className="max-h-40 space-y-1 overflow-y-auto rounded-md border border-white/10 bg-white/[0.03] p-3 text-xs text-bf-muted">
            <div className="text-bf-text">
              Will add {previewCount} camera{previewCount === 1 ? "" : "s"}
              {kind === "nvr" &&
                ` on channels ${channelPreview[0]}–${channelPreview[channelPreview.length - 1]}`}
              .
            </div>
            {kind === "direct" &&
              directPreview.slice(0, 8).map((u) => (
                <div key={u} className="break-all font-mono">
                  {u}
                </div>
              ))}
            {kind === "direct" && directPreview.length > 8 && (
              <div>… and {directPreview.length - 8} more</div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="bf-button bg-white/10 bg-none" onClick={onClose}>
            Cancel
          </button>
          <button
            className="bf-button disabled:opacity-40"
            type="submit"
            disabled={previewCount === 0 || !store.trim()}
          >
            Add {previewCount || ""} camera{previewCount === 1 ? "" : "s"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
